import React, { useContext } from "react";
import ReusableDropdown from "../../../../components/ReusableDropdown";
import { LanguageContext } from "../../../../constant/LanguageContext";
import { Download, FileText, Calendar as CalendarIcon, X } from "lucide-react";

const ExportModal = ({
  show,
  exportFormat,
  setExportFormat,
  exportDateRange,
  setExportDateRange,
  loading,
  t,
  styles,
  screenSize,
  theme,
  onClose,
  onExport,
  handleCardHover,
  handleButtonHover,
}) => {
  const { language } = useContext(LanguageContext);
  if (!show) return null;

  const exportOptions = [
    {
      id: "pdf",
      name: t.pdfReport,
      icon: FileText,
      description: t.formattedReport,
    },
    {
      id: "excel",
      name: t.excelFile,
      icon: Download,
      description: t.spreadsheetData,
    },
    {
      id: "ical",
      name: t.calendarFile,
      icon: CalendarIcon,
      description: t.importCalendars,
    },
  ];

  return (
    <div style={styles.modalOverlay}>
      <div style={styles.modalContainer}>
        <div style={styles.modalHeader}>
          <div style={styles.modalTitleSection}>
            <div style={styles.modalIcon}>
              <Download size={screenSize.isMobile ? 24 : 28} />
            </div>
            <div>
              <h2 style={styles.modalTitle}>{t.exportCalendar}</h2>
              <p style={styles.modalSubtitle}>{t.chooseFormat}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={styles.closeButton}
            onMouseEnter={(e) => handleButtonHover(e, true)}
            onMouseLeave={(e) => handleButtonHover(e, false)}
          >
            <X size={screenSize.isMobile ? 20 : 24} />
          </button>
        </div>

        <div style={styles.formContainer}>
          <div style={{ marginBottom: screenSize.isMobile ? "16px" : "24px" }}>
            <label style={styles.formLabel}>{t.exportFormat}</label>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: screenSize.isMobile
                  ? "1fr"
                  : "repeat(auto-fit, minmax(150px, 1fr))",
                gap: screenSize.isMobile ? "12px" : "16px",
                marginBottom: screenSize.isMobile ? "16px" : "24px",
              }}
            >
              {exportOptions.map((option) => (
                <div
                  key={option.id}
                  style={{
                    padding: screenSize.isMobile ? "16px" : "20px",
                    borderRadius: screenSize.isMobile ? "12px" : "16px",
                    border:
                      exportFormat === option.id
                        ? "2px solid #2e7cba"
                        : theme === "dark"
                        ? "1px solid rgba(255,255,255,0.1)"
                        : "1px solid rgba(0,0,0,0.1)",
                    background:
                      exportFormat === option.id
                        ? "rgba(59, 130, 246, 0.1)"
                        : theme === "dark"
                        ? "rgba(255,255,255,0.02)"
                        : "rgba(255,255,255,0.5)",
                    cursor: "pointer",
                    textAlign: "center",
                    transition: "all 0.3s ease",
                  }}
                  onClick={() => setExportFormat(option.id)}
                  onMouseEnter={(e) => handleCardHover(e, true)}
                  onMouseLeave={(e) => handleCardHover(e, false)}
                >
                  <div
                    style={{
                      width: screenSize.isMobile ? "40px" : "48px",
                      height: screenSize.isMobile ? "40px" : "48px",
                      margin: "0 auto 12px",
                      borderRadius: screenSize.isMobile ? "10px" : "12px",
                      background: "rgba(59, 130, 246, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#2e7cba",
                    }}
                  >
                    <option.icon size={screenSize.isMobile ? 20 : 24} />
                  </div>
                  <div
                    style={{
                      fontSize: screenSize.isMobile ? "13px" : "14px",
                      fontWeight: "600",
                      color: theme === "dark" ? "#fff" : "#1a1a1a",
                      marginBottom: "4px",
                    }}
                  >
                    {option.name}
                  </div>
                  <div
                    style={{
                      fontSize: screenSize.isMobile ? "11px" : "12px",
                      color:
                        theme === "dark"
                          ? "rgba(255,255,255,0.6)"
                          : "rgba(26,26,26,0.5)",
                    }}
                  >
                    {option.description}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: screenSize.isMobile ? "16px" : "24px" }}>
            <label style={styles.formLabel}>{t.dateRange}</label>
            <ReusableDropdown
              items={[
                { id: "this_week", label: t.thisWeekRange },
                { id: "this_month", label: t.thisMonthRange },
                { id: "last_month", label: t.lastMonth },
                { id: "this_quarter", label: t.thisQuarter },
                { id: "this_year", label: t.thisYear },
                { id: "all", label: t.allEventsRange },
              ]}
              value={exportDateRange}
              onChange={setExportDateRange}
              getItemKey={(item) => item.id}
              getItemLabel={(item) => item.label}
              searchable={false}
              theme={theme}
              language={language}
            />
          </div>

          <div style={styles.formActions}>
            <button
              onClick={onClose}
              style={styles.actionButton("default")}
              onMouseEnter={(e) => handleButtonHover(e, true)}
              onMouseLeave={(e) => handleButtonHover(e, false)}
            >
              {t.cancel}
            </button>
            <button
              onClick={onExport}
              disabled={loading}
              style={styles.actionButton("primary")}
              onMouseEnter={(e) => handleButtonHover(e, true, "primary")}
              onMouseLeave={(e) => handleButtonHover(e, false, "primary")}
            >
              <Download size={16} />
              {loading ? t.exporting : t.export}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;
